import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  Grid,
  Paper,
} from '@mui/material';
import { adminApi } from '../api';

const empty = {
  companyName: '',
  primaryColor: '#1f4e79',
  secondaryColor: '#00a3a1',
  fontFamily: 'Montserrat, sans-serif',
  logoUrl: '',
  avatarUrl: '',
  introVideoUrl: '',
  welcomeMessage: '',
  embedPublicUrl: '',
  brandSttAliasesText: '',
  openaiApiKey: '',
};

export default function SettingsPage() {
  const [form, setForm] = useState(empty);
  const [hasOpenaiApiKey, setHasOpenaiApiKey] = useState(false);
  const [openaiApiKeyMasked, setOpenaiApiKeyMasked] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const applySettings = (data) => {
    const aliases = Array.isArray(data.brandSttAliases)
      ? data.brandSttAliases.map((item) => `${item.from}=${item.to}`).join('\n')
      : '';
    setHasOpenaiApiKey(Boolean(data.hasOpenaiApiKey));
    setOpenaiApiKeyMasked(data.openaiApiKeyMasked || null);
    setForm({
      companyName: data.companyName || '',
      primaryColor: data.primaryColor || '#1f4e79',
      secondaryColor: data.secondaryColor || '#00a3a1',
      fontFamily: data.fontFamily || 'Montserrat, sans-serif',
      logoUrl: data.logoUrl || '',
      avatarUrl: data.avatarUrl || '',
      introVideoUrl: data.introVideoUrl || '',
      welcomeMessage: data.welcomeMessage || '',
      embedPublicUrl: data.embedPublicUrl || '',
      brandSttAliasesText: aliases,
      openaiApiKey: '',
    });
  };

  useEffect(() => {
    adminApi.getSettings().then(applySettings).catch((err) => setError(err.message));
  }, []);

  const setField = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const uploadTo = async (field, file) => {
    if (!file) return;
    try {
      const data = await adminApi.upload(file);
      setForm((prev) => ({ ...prev, [field]: data.url }));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const brandSttAliases = form.brandSttAliasesText
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
          const [from, ...rest] = line.split('=');
          return { from: from.trim(), to: rest.join('=').trim() };
        })
        .filter((item) => item.from && item.to);

      const saved = await adminApi.saveSettings({
        ...form,
        brandSttAliases,
        pronunciationMap: {},
        openaiApiKey: form.openaiApiKey.trim() || undefined,
      });
      applySettings(saved);
      setMessage('Impostazioni salvate.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSave}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>
        Branding
      </Typography>
      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Chiave API OpenAI</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Il cliente può inserire qui la propria chiave. Ha priorità rispetto a quella nel file .env.
          {hasOpenaiApiKey && openaiApiKeyMasked ? ` Chiave attuale: ${openaiApiKeyMasked}` : ' Nessuna chiave salvata nel gestionale.'}
        </Typography>
        <TextField
          label="Nuova chiave API"
          type="password"
          value={form.openaiApiKey}
          onChange={setField('openaiApiKey')}
          fullWidth
          autoComplete="off"
          placeholder="sk-..."
          helperText="Lascia vuoto per non modificare la chiave già salvata."
        />
        {hasOpenaiApiKey && (
          <Button
            color="warning"
            sx={{ mt: 1 }}
            onClick={async () => {
              if (!window.confirm('Rimuovere la chiave API dal gestionale?')) return;
              try {
                const saved = await adminApi.saveSettings({ ...form, openaiApiKey: undefined, clearOpenaiApiKey: true });
                applySettings(saved);
                setMessage('Chiave API rimossa.');
              } catch (err) {
                setError(err.message);
              }
            }}
          >
            Rimuovi chiave
          </Button>
        )}
      </Paper>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField label="Nome azienda" value={form.companyName} onChange={setField('companyName')} fullWidth />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField label="Colore primario" type="color" value={form.primaryColor} onChange={setField('primaryColor')} fullWidth />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField label="Colore secondario" type="color" value={form.secondaryColor} onChange={setField('secondaryColor')} fullWidth />
        </Grid>
        <Grid item xs={12}>
          <TextField label="Font CSS" value={form.fontFamily} onChange={setField('fontFamily')} fullWidth />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Messaggio di benvenuto"
            value={form.welcomeMessage}
            onChange={setField('welcomeMessage')}
            fullWidth
            multiline
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField label="URL logo" value={form.logoUrl} onChange={setField('logoUrl')} fullWidth sx={{ mb: 1 }} />
          <Button component="label" variant="outlined">
            Carica logo
            <input hidden type="file" accept="image/*" onChange={(e) => uploadTo('logoUrl', e.target.files[0])} />
          </Button>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField label="URL avatar chat" value={form.avatarUrl} onChange={setField('avatarUrl')} fullWidth sx={{ mb: 1 }} />
          <Button component="label" variant="outlined">
            Carica avatar
            <input hidden type="file" accept="image/*" onChange={(e) => uploadTo('avatarUrl', e.target.files[0])} />
          </Button>
        </Grid>
        <Grid item xs={12}>
          <TextField label="URL video intro" value={form.introVideoUrl} onChange={setField('introVideoUrl')} fullWidth sx={{ mb: 1 }} />
          <Button component="label" variant="outlined">
            Carica video
            <input hidden type="file" accept="video/mp4,video/webm" onChange={(e) => uploadTo('introVideoUrl', e.target.files[0])} />
          </Button>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="URL pubblico del widget (per lo snippet embed)"
            value={form.embedPublicUrl}
            onChange={setField('embedPublicUrl')}
            fullWidth
            placeholder="https://tuodominio.com"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Alias riconoscimento vocale (una riga: errore=NomeBrand)"
            value={form.brandSttAliasesText}
            onChange={setField('brandSttAliasesText')}
            fullWidth
            multiline
            minRows={3}
            helperText="Esempio: igenia=Hygenia"
          />
        </Grid>
      </Grid>
      <Box sx={{ mt: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <Button type="submit" variant="contained" disabled={saving}>
          {saving ? 'Salvataggio...' : 'Salva'}
        </Button>
        <Paper sx={{ px: 2, py: 1, bgcolor: form.primaryColor, color: '#fff' }}>Primario</Paper>
        <Paper sx={{ px: 2, py: 1, bgcolor: form.secondaryColor, color: '#fff' }}>Secondario</Paper>
      </Box>
    </Box>
  );
}
